import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, PlayCircle, Eye, RefreshCw, Search, Sparkles, Link2, MoreVertical, Trash2, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SimulateFixtureModal from "@/components/SimulateFixtureModal";
import SupplierInvoiceDetail from "@/components/SupplierInvoiceDetail";
import SupplierInvoiceSourceBadge from "@/components/SupplierInvoiceSourceBadge";
import SupplierInvoiceManualForm from "@/components/SupplierInvoiceManualForm";
import DuplicateInvoiceModal from "@/components/DuplicateInvoiceModal";

const money = (n) =>
  new Intl.NumberFormat("fr-BE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const shortDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-BE", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const TABS = [
  { key: "received", label: "Reçues" },
  { key: "validated", label: "Validées" },
  { key: "paid", label: "Payées" },
];

export default function SupplierInvoicesPage() {
  const { state } = useAuth();
  const [status, setStatus] = useState("received");
  const [category, setCategory] = useState("all"); // Phase Rentab-1+2-B — filtre catégorie
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openSimulate, setOpenSimulate] = useState(false);
  const [openManual, setOpenManual] = useState(false);
  const [duplicateConflict, setDuplicateConflict] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mockMode, setMockMode] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: "100" });
      if (search) params.set("search", search);
      const { data } = await api.get(`/supplier-invoices?${params}`);
      let list = data.items || [];
      // Filtre côté client — évite un round-trip et un endpoint dédié pour B.
      if (category !== "all") {
        if (category === "unassigned") {
          list = list.filter((it) => !it.category);
        } else {
          list = list.filter((it) => it.category === category);
        }
      }
      setItems(list);
      setTotal(data.total || list.length);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status, search, category]);

  useEffect(() => { load(); }, [load]);

  // Phase Rentab-1+2-B — plus d'inline accept (nécessite category obligatoire).
  // Le user doit ouvrir le détail pour valider la category via la modal.
  const openDetailForLink = (inv) => setDetail(inv);

  // Correctif 10a-fix — Suppression facture (garde-fous côté backend)
  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/supplier-invoices/${deleteTarget.id}`);
      toast.success("Facture supprimée définitivement.");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    // Détecte mock mode via l'endpoint fixtures (403 si live)
    api.get("/admin/peppol/fixtures")
      .then(({ data }) => setMockMode(!!data.mock_mode))
      .catch(() => setMockMode(false));
    // Bug #2 fix (14/09/2026) — refetch quand company change dans le même onglet.
  }, [state?.company?.id]);

  // C1A v2 (15/09/2026) — Retrait du bypass localStorage("werko_token")
  // (clé legacy jamais écrite → Authorization: Bearer null → download PDF/UBL
  // cassé silencieusement). `SupplierInvoiceDetail` utilise désormais
  // getToken() en interne + `.catch()` avec toast visible.

  return (
    <div className="space-y-6 p-6" data-testid="supplier-invoices-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            Factures fournisseurs
            {mockMode && (
              <Badge className="border border-amber-500/50 bg-amber-500/10 text-amber-300 text-xs font-normal">
                Peppol MOCKED — branchement réel en cours
              </Badge>
            )}
          </h1>
          <p className="text-sm text-slate-400">Boîte de réception des factures fournisseurs UBL reçues via Peppol.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} className="border-slate-700 bg-slate-800 text-slate-200" data-testid="reload-btn">
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Rafraîchir
          </Button>
          <Button
            onClick={() => setOpenManual(true)}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="add-manual-btn"
          >
            <Plus className="mr-1 h-4 w-4" /> Ajouter manuellement
          </Button>
          {mockMode && (
            <Button
              onClick={() => setOpenSimulate(true)}
              variant="outline"
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="simulate-btn"
            >
              <PlayCircle className="mr-1 h-4 w-4" /> Simuler réception UBL (test)
            </Button>
          )}
        </div>
      </div>

      <Tabs value={status} onValueChange={setStatus} data-testid="supplier-invoices-tabs">
        <TabsList className="border border-slate-800 bg-slate-900">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`tab-${t.key}`}
              className="data-[state=active]:bg-[#C4553B] data-[state=active]:text-white"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (fournisseur, n° facture, comm.)"
            className="pl-8 border-slate-700 bg-slate-950/60"
            data-testid="search-input"
          />
        </div>
        {/* Phase Rentab-1+2-B — Filtre catégorie */}
        <div className="flex items-center gap-1" data-testid="category-filter-group">
          {[
            { value: "all", label: "Toutes" },
            { value: "material", label: "Matériel" },
            { value: "subcontracting", label: "Sous-traitance" },
            { value: "unassigned", label: "Non catégorisées" },
          ].map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setCategory(f.value)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                category === f.value
                  ? "border-[#C4553B] bg-[#C4553B]/20 text-[#C4553B]"
                  : "border-slate-700 bg-slate-950/40 text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`category-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-slate-500" data-testid="supplier-invoices-empty">
              Aucune facture {TABS.find(t => t.key === status)?.label.toLowerCase()} pour le moment.
              {mockMode && status === "received" && <div className="mt-1 text-xs">Utilisez « Simuler » pour tester le flux.</div>}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="supplier-invoices-table">
              <thead className="border-b border-slate-800 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="p-3 text-left">Fournisseur</th>
                  <th className="p-3 text-left">Source</th>
                  <th className="p-3 text-left">N° facture</th>
                  <th className="p-3 text-left">Émission</th>
                  <th className="p-3 text-left">Échéance</th>
                  <th className="p-3 text-right">Montant TVAC</th>
                  <th className="p-3 text-left">Catégorie</th>
                  <th className="p-3 text-left">Chantier</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-800/30" data-testid={`row-${it.id}`}>
                    <td className="p-3 font-medium">{it.supplier_name || "—"}</td>
                    <td className="p-3">
                      <SupplierInvoiceSourceBadge source={it.source} />
                    </td>
                    <td className="p-3 font-mono text-xs">{it.invoice_number}</td>
                    <td className="p-3 text-slate-400">{shortDate(it.issue_date)}</td>
                    <td className="p-3 text-slate-400">{shortDate(it.due_date)}</td>
                    <td className="p-3 text-right tabular-nums font-medium">{money(it.amount_tvac)}</td>
                    <td className="p-3">
                      {it.category === "material" ? (
                        <Badge
                          className="border border-slate-600/40 bg-slate-700/20 text-slate-300 text-xs"
                          data-testid={`row-category-${it.id}`}
                        >
                          Matériel
                        </Badge>
                      ) : it.category === "subcontracting" ? (
                        <Badge
                          className="border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs"
                          data-testid={`row-category-${it.id}`}
                        >
                          Sous-traitance
                        </Badge>
                      ) : (
                        <span
                          className="text-xs italic text-slate-500"
                          data-testid={`row-category-${it.id}`}
                        >
                          Non catégorisée
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {it.linked_chantier_id ? (
                        <Badge
                          className="border border-emerald-600/40 bg-emerald-500/10 text-emerald-300 text-xs"
                          data-testid={`row-linked-${it.id}`}
                        >
                          <Link2 className="mr-1 h-3 w-3" />
                          {it.linked_chantier_name || "Lié"}
                        </Badge>
                      ) : it.auto_link_suggestion_id && !it.auto_link_dismissed ? (
                        <div
                          className="inline-flex flex-col gap-1"
                          data-testid={`row-suggestion-${it.id}`}
                        >
                          <Badge className="border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xs font-normal">
                            <Sparkles className="mr-1 h-3 w-3" />
                            Proposition : {it.auto_link_suggestion_name || "chantier"}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetailForLink(it)}
                            className="h-6 border-emerald-600/50 bg-emerald-600/10 px-2 py-0 text-xs text-emerald-300 hover:bg-emerald-600 hover:text-white"
                            data-testid={`row-open-link-${it.id}`}
                          >
                            Rattacher…
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Non rattaché</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          onClick={() => setDetail(it)}
                          variant="outline"
                          size="sm"
                          className="border-slate-700 bg-slate-800 text-slate-200"
                          data-testid={`view-${it.id}`}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" /> Voir
                        </Button>
                        {(it.status === "received" || !it.status) && !it.linked_chantier_id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-slate-700 bg-slate-800 px-2 text-slate-200"
                                data-testid={`row-menu-${it.id}`}
                              >
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="border-slate-700 bg-slate-900 text-slate-100"
                            >
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(it)}
                                className="cursor-pointer text-red-300 focus:bg-red-600 focus:text-white"
                                data-testid={`row-delete-${it.id}`}
                              >
                                <Trash2 className="mr-2 h-3.5 w-3.5" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {total > items.length && (
        <div className="text-xs text-slate-500">{items.length} / {total} affichées</div>
      )}

      <SimulateFixtureModal
        open={openSimulate}
        onOpenChange={setOpenSimulate}
        onSuccess={() => load()}
      />
      <SupplierInvoiceManualForm
        open={openManual}
        onOpenChange={setOpenManual}
        onCreated={(inv) => {
          load();
          setDetail(inv);
        }}
        onDuplicate={(conflict) => setDuplicateConflict(conflict)}
      />
      <DuplicateInvoiceModal
        open={!!duplicateConflict}
        onOpenChange={(o) => !o && setDuplicateConflict(null)}
        conflict={duplicateConflict}
        onViewExisting={async (existingId) => {
          try {
            const { data } = await api.get(`/supplier-invoices/${existingId}`);
            setDuplicateConflict(null);
            setDetail(data);
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
      />
      <SupplierInvoiceDetail
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        invoice={detail}
        onChanged={load}
      />

      {/* Correctif 10a-fix — Confirmation suppression depuis la liste (menu 3-points) */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent
          className="border-slate-800 bg-slate-900 text-slate-100"
          data-testid="list-delete-confirm-modal"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement la facture ?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Supprimer définitivement la facture{" "}
              <span className="font-mono text-slate-200">N°{deleteTarget?.invoice_number}</span> de{" "}
              <span className="font-medium text-slate-200">{deleteTarget?.supplier_name}</span> ?
              <br />
              <span className="mt-2 block text-red-400/90 text-xs">
                Cette action est irréversible. Seule une trace anonymisée restera dans l&apos;audit log.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="list-delete-cancel"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-500"
              data-testid="list-delete-confirm"
            >
              {deleting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
